import { useMemo, useState } from 'react';
import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import { Box, Chip, Stack, Typography } from '@mui/material';

import { sortIntakeItemsByStatus } from '../domain';
import type { IntakeItem, IntakeItemStatus } from '../types';

import type { IntakeDocumentAccess } from './IntakeItemDetailsDialog';
import { IntakeItemRow } from './IntakeItemRow';

interface IntakeItemListSectionProps {
  title: string;
  items: IntakeItem[];
  emptyLabel?: string;
  compact?: boolean;
  columns?: 1 | 2;
  defaultFilter?: IntakeItemFilter;
  documentAccess?: IntakeDocumentAccess;
  onUploadRequest?: (item?: IntakeItem) => void;
  scroll?: boolean;
}

type IntakeItemFilter = 'open' | 'needs_action' | 'missing' | 'in_review' | 'complete' | 'all';

const COMPLETE_STATUSES = new Set<IntakeItemStatus>([
  'accepted',
  'staff_complete',
  'not_applicable',
]);
const NEEDS_ACTION_STATUSES = new Set<IntakeItemStatus>([
  'ai_flagged',
  'expiring',
  'needs_attention',
]);
const IN_REVIEW_STATUSES = new Set<IntakeItemStatus>([
  'in_review',
  'staff_in_progress',
  'submitted',
]);

const FILTERS: Array<{ value: IntakeItemFilter; label: string }> = [
  { value: 'open', label: 'Open' },
  { value: 'needs_action', label: 'Needs action' },
  { value: 'missing', label: 'Missing' },
  { value: 'in_review', label: 'In review' },
  { value: 'complete', label: 'Complete' },
  { value: 'all', label: 'All' },
];

function itemMatchesFilter(item: IntakeItem, filter: IntakeItemFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'open') return !COMPLETE_STATUSES.has(item.status);
  if (filter === 'needs_action') return NEEDS_ACTION_STATUSES.has(item.status);
  if (filter === 'missing') return item.status === 'missing';
  if (filter === 'in_review') return IN_REVIEW_STATUSES.has(item.status);
  return COMPLETE_STATUSES.has(item.status);
}

function countItems(items: IntakeItem[], filter: IntakeItemFilter): number {
  return items.filter(item => itemMatchesFilter(item, filter)).length;
}

export function IntakeItemListSection({
  title,
  items,
  emptyLabel = 'Nothing here right now.',
  compact = false,
  columns = 2,
  defaultFilter = countItems(items, 'open') > 0 ? 'open' : 'all',
  documentAccess,
  onUploadRequest,
  scroll = false,
}: IntakeItemListSectionProps) {
  const [filter, setFilter] = useState<IntakeItemFilter>(defaultFilter);
  const sortedItems = useMemo(() => sortIntakeItemsByStatus(items), [items]);
  const visibleItems = useMemo(
    () => sortedItems.filter(item => itemMatchesFilter(item, filter)),
    [filter, sortedItems],
  );
  const openCount = countItems(items, 'open');
  const completeCount = countItems(items, 'complete');

  if (items.length === 0) {
    return null;
  }

  return (
    <Stack spacing={0.75} sx={{ minWidth: 0 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={0.75}
        sx={{ justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' } }}
      >
        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 900 }}>
          {title} · {openCount} open · {completeCount} complete
        </Typography>
        <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
          {FILTERS.map(option => {
            const count = countItems(items, option.value);
            return (
              <Chip
                key={option.value}
                size="small"
                label={`${option.label} ${count}`}
                clickable
                color={filter === option.value ? 'primary' : 'default'}
                variant={filter === option.value ? 'filled' : 'outlined'}
                onClick={() => setFilter(option.value)}
                sx={{
                  height: 24,
                  borderRadius: '6px',
                  fontWeight: 750,
                  ...(filter === option.value
                    ? {
                        bgcolor: ELIG_TOKENS.midnight,
                        color: '#fff',
                        '&:hover': { bgcolor: ELIG_TOKENS.midnight, filter: 'brightness(0.94)' },
                      }
                    : {
                        color: ELIG_TOKENS.inkSoft,
                        borderColor: ELIG_TOKENS.lineStrong,
                        bgcolor: 'transparent',
                        '&:hover': { bgcolor: ELIG_TOKENS.grayTint },
                      }),
                }}
              />
            );
          })}
        </Stack>
      </Stack>

      <Box
        sx={{
          ...(scroll
            ? {
                height: { xs: 260, lg: 300 },
                maxHeight: { xs: 260, lg: 300 },
                minHeight: 0,
                overflowX: 'hidden',
                overflowY: 'auto',
                overscrollBehavior: 'contain',
                pr: 0.5,
                scrollbarWidth: 'thin',
              }
            : undefined),
        }}
      >
        {visibleItems.length > 0 ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns:
                columns === 1 ? '1fr' : { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
              gap: compact ? 0.6 : 0.75,
            }}
          >
            {visibleItems.map(item => (
              <IntakeItemRow
                key={item.id}
                item={item}
                compact={compact}
                documentAccess={documentAccess}
                onUploadRequest={onUploadRequest}
              />
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {emptyLabel}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}
